import parseExpiresAt from "../utils/sessionTime";
import { deriveUserFromToken } from "../utils/jwt";

let session = null;
const listeners = new Set();

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener(session);
    } catch {
      // ignore listener errors so others still run
    }
  });
};

const normalizeRoles = (roles) => {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles;
  return [roles];
};

const normalizeUser = (user) => {
  if (!user) return null;
  return {
    ...user,
    roles: normalizeRoles(user.roles),
  };
};

const normalizeMemberships = (memberships) => {
  if (!Array.isArray(memberships)) return [];
  return memberships
    .filter((m) => m && m.businessId != null)
    .map((m) => ({
      ...m,
      roles: normalizeRoles(m.roles),
    }));
};

const buildSession = (raw) => {
  if (!raw) return null;
  const expiresAt = parseExpiresAt(raw.expiresAt);
  const token = raw.token || "";
  let user = normalizeUser(raw.user ?? session?.user ?? null);
  if (!user && token) {
    user = normalizeUser(deriveUserFromToken(token));
  }
  return {
    token,
    expiresAt,
    user,
    activeBusinessId: raw.activeBusinessId ?? null,
    memberships: normalizeMemberships(raw.memberships ?? session?.memberships ?? []),
  };
};

export const getSession = () => session;

export const getToken = () => session?.token || "";

export const getExpiresAt = () => session?.expiresAt || 0;

export const setSession = (raw) => {
  session = buildSession(raw);
  notify();
  return session;
};

export const updateSession = (updater) => {
  const next = typeof updater === "function" ? updater(session) : updater;
  if (!next) {
    return setSession(null);
  }
  const merged = {
    ...(session || {}),
    ...next,
  };
  if (next.user !== undefined) {
    merged.user = normalizeUser(next.user);
  }
  if (next.expiresAt !== undefined) {
    merged.expiresAt = parseExpiresAt(next.expiresAt);
  }
  if (next.token !== undefined) {
    merged.token = next.token || "";
  }
  if (next.activeBusinessId !== undefined) {
    merged.activeBusinessId = next.activeBusinessId ?? null;
  }
  if (next.memberships !== undefined) {
    merged.memberships = normalizeMemberships(next.memberships);
  }
  return setSession(merged);
};

export const clearSession = () => {
  setSession(null);
};

export const subscribeToSession = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
